var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import cx from 'classnames';
import styles from './Stars.module.scss';
import { useSafeId } from '../../../core/hooks/useSafeId';
import { StarHalfSelected, StarSelected, StarUnselected } from '../../../foundations/icons';
var generateNthStar = function (n, roundedRating, starSize) {
    if (roundedRating > n + 0.5)
        return React.createElement(StarSelected, { size: starSize });
    if (roundedRating > n)
        return React.createElement(StarHalfSelected, { size: starSize });
    return React.createElement(StarUnselected, { size: starSize });
};
export var Stars = function (_a) {
    var _b, _c;
    var rating = _a.rating, className = _a.className, hasSchema = _a.hasSchema, _d = _a.starSize, starSize = _d === void 0 ? 'xsmall' : _d, _e = _a.formAriaLabel, formAriaLabel = _e === void 0 ? 'rating' : _e, onHighlight = _a.onHighlight, _f = _a.submitHandler, submitHandler = _f === void 0 ? function () { } : _f, props = __rest(_a, ["rating", "className", "hasSchema", "starSize", "formAriaLabel", "onHighlight", "submitHandler"]);
    var formId = useSafeId();
    var starsClassList = cx(styles.stars, className, (_b = {},
        _b[styles.large] = starSize === 'large',
        _b[styles.interactive] = !rating && onHighlight,
        _b));
    var starRatingClass = cx(styles.starRating, (_c = {},
        _c[styles.large] = starSize === 'large',
        _c));
    // Readonly stars logic
    var roundRating = Math.round(rating * 2) / 2;
    var starRatingIcons = [
        generateNthStar(0, roundRating, starSize),
        generateNthStar(1, roundRating, starSize),
        generateNthStar(2, roundRating, starSize),
        generateNthStar(3, roundRating, starSize),
        generateNthStar(4, roundRating, starSize),
    ];
    // Interactive stars logic
    var _g = useState(0), starRating = _g[0], setStarRating = _g[1];
    var handleClickOrEnter = function (star) {
        setStarRating(star);
        onHighlight && onHighlight(star);
    };
    var handleLeave = function () {
        setStarRating(0);
        onHighlight && onHighlight('');
    };
    var handleSubmit = function (e) {
        submitHandler();
        e.preventDefault();
    };
    var isReadOnly = rating >= 0 && !onHighlight;
    return (React.createElement("span", __assign({ className: starsClassList }, props),
        isReadOnly ? (starRatingIcons.map(function (icon, index) { return (React.createElement("span", { key: "star-".concat(index + 1), className: styles.star, "data-testid": "star" }, icon)); })) : (React.createElement("form", { "aria-label": formAriaLabel, className: styles.starRatingForm, onSubmit: handleSubmit },
            starRatingIcons.map(function (_, index) {
                var id = "".concat(formId, "-").concat(index);
                var star = index + 1;
                return (React.createElement("span", { key: star },
                    React.createElement("input", { type: "radio", name: "rating", id: id, className: styles.visuallyhidden, onClick: function (e) {
                            handleClickOrEnter(star);
                            /*
                             * Separate paths here for pointer and keyboard usage.
                             *  - for pointer users (`e.clientX` is truthy), submission is triggered early
                             *    when a star is clicked
                             *  - for keyboard users, selection is performed using UP/DOWN, then submission
                             *    is performed using ENTER
                             */
                            e.clientX > 0 && handleSubmit(e);
                        } }),
                    React.createElement("label", { htmlFor: id, className: starRatingClass, onMouseEnter: function () { return handleClickOrEnter(star); }, onMouseLeave: handleLeave },
                        star <= starRating ? (React.createElement(StarSelected, { className: styles.star, size: starSize })) : (React.createElement(StarUnselected, { className: styles.star, size: starSize })),
                        React.createElement("span", { className: styles.visuallyhidden }, "".concat(star, " ").concat(star > 1 ? 'Stars' : 'Star')))));
            }),
            React.createElement("button", { type: "submit", className: styles.visuallyhidden }, "Submit"))),
        React.createElement("span", { className: styles.srOnly },
            hasSchema ? React.createElement("span", { itemProp: "ratingValue" }, roundRating) : roundRating,
            " out of 5 stars")));
};
//# sourceMappingURL=Stars.js.map